import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Lenis from "lenis";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import LegalPage from "./LegalPage";
import { 
  Settings, 
  Database, 
  Cpu, 
  Target, 
  ChevronRight, 
  Menu, 
  X, 
  ArrowUpRight,
  Atom,
  Factory,
  Gem,
  Plane,
  Building2,
  Pickaxe,
  Linkedin,
  Mail,
  Phone,
  Search,
  ListChecks,
  Rocket,
  TrendingUp,
  Quote,
  CheckCircle2,
  Copy,
  Clock,
  Users,
  BarChart3,
  Zap,
  Globe,
  UserCircle,
  Handshake
} from "lucide-react";

// Logo ADAPEL - with zoom-safe styling
const Logo = ({ className = "" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_site-hostinger/artifacts/qhamtoxf_ADAPEL%20LOGO%202024%20500%20X%20500.png" 
    alt="ADAPEL" 
    className={`h-10 w-auto flex-shrink-0 ${className}`}
    style={{ imageRendering: 'crisp-edges' }}
  />
);

// Animated Counter Component - only for pure numbers
const AnimatedCounter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [displayValue, setDisplayValue] = useState(value);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  useEffect(() => {
    if (isInView) {
      // Extract numeric part and detect format
      const match = value.match(/^(\+?)(\d+)(\+?%?)$/);
      if (match) {
        const prefix = match[1];
        const numericValue = parseInt(match[2]);
        const suffixPart = match[3];
        
        const duration = 1500;
        const steps = 40;
        const increment = numericValue / steps;
        let current = 0;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= numericValue) {
            setDisplayValue(`${prefix}${numericValue}${suffixPart}`);
            clearInterval(timer);
          } else {
            setDisplayValue(`${prefix}${Math.floor(current)}${suffixPart}`);
          }
        }, duration / steps);
        
        return () => clearInterval(timer);
      }
    }
  }, [isInView, value]);
  
  return (
    <span ref={ref} className="tabular-nums">
      {displayValue}
    </span>
  );
};

// Navigation
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#approche", label: t.nav.approche },
    { href: "#expertises", label: t.nav.expertises },
    { href: "#realisations", label: t.nav.missions },
    { href: "#cas-client", label: t.nav.casClient },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      // On mobile, wait for menu close animation before scrolling
      if (isMobileOpen) {
        setIsMobileOpen(false);
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 350);
      } else {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="fixed top-6 left-0 right-0 z-50 px-4 md:px-6"
      style={{ transformOrigin: 'center top' }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto">
        <div className={`glass rounded-full px-4 md:px-8 py-3 flex items-center justify-between transition-all duration-500 ${
          isScrolled ? "shadow-lg shadow-black/20" : ""
        }`}>
          <a href="#top" onClick={(e) => scrollToSection(e, "#hero")} className="flex items-center gap-3 flex-shrink-0">
            <Logo />
            <span className="font-semibold text-lg tracking-tight hidden md:block whitespace-nowrap">ADAPEL</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8 flex-shrink-0">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-sm text-[var(--text-secondary)] hover:text-white transition-colors duration-300 relative group whitespace-nowrap"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4 flex-shrink-0">
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, "#contact")}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="nav-cta"
            >
              {t.nav.cta}
            </a>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--border)] hover:border-[var(--primary)] transition-colors text-sm"
              data-testid="language-toggle"
              aria-label="Toggle language"
            >
              <Globe size={16} className="text-[var(--secondary)]" />
              <span className="font-medium">{language.toUpperCase()}</span>
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{ 
          height: isMobileOpen ? "auto" : 0,
          opacity: isMobileOpen ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
        className="md:hidden overflow-hidden"
      >
        <div className="glass mt-2 rounded-2xl p-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              className="text-lg text-white hover:text-[var(--primary)] transition-colors py-3 border-b border-[var(--border)] font-medium"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, "#contact")}
            className="btn-primary text-center mt-4 py-4 text-lg font-semibold"
          >
            {t.nav.cta}
          </a>
          
          {/* Mobile Language Toggle - More visible */}
          <button
            onClick={() => {
              toggleLanguage();
              setIsMobileOpen(false);
            }}
            className="flex items-center justify-center gap-3 mt-2 py-4 bg-[var(--primary)] hover:bg-[var(--primary-light)] text-white rounded-full font-semibold transition-colors"
            data-testid="mobile-language-toggle"
          >
            <Globe size={20} />
            <span>{language === 'fr' ? 'Switch to English' : 'Passer en Français'}</span>
          </button>
        </div>
      </motion.div>
    </motion.nav>
  );
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = "", id = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const Hero = () => {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section 
      ref={ref} 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 pb-20"
      data-testid="hero-section"
    >
      {/* Background */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1720036236697-018370867320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmdXR1cmlzdGljJTIwaW5kdXN0cmlhbCUyMGZhY3RvcnklMjBpbnRlcmlvciUyMGRhcmslMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzcyNzUwMTU1fDA&ixlib=rb-4.1.0&q=85')`,
            filter: "brightness(0.3)"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-default)] via-transparent to-[var(--bg-default)]" />
      </motion.div>
      
      {/* Hero Glow */}
      <div className="hero-glow" />

      {/* Content */}
      <motion.div 
        style={{ opacity }}
        className="relative z-10 max-w-5xl mx-auto px-6 text-center"
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="inline-flex items-center gap-2 glass rounded-full px-5 py-2 mb-8"
        >
          <span className="w-2 h-2 bg-[var(--secondary)] rounded-full animate-pulse"></span>
          <span className="mono text-xs text-[var(--text-secondary)] uppercase tracking-wider">
            {t.hero.badge}
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8"
        >
          {t.hero.title1}{" "}
          <span className="gradient-text">{t.hero.titleHighlight1}</span>.<br />
          {t.hero.title2}{" "}
          <span className="gradient-text">{t.hero.titleHighlight2}</span>.
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto mb-12 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <a href="#contact" className="btn-primary flex items-center gap-2 group" data-testid="hero-cta-primary">
            {t.hero.cta1}
            <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </a>
          <a href="#realisations" className="btn-ghost flex items-center gap-2" data-testid="hero-cta-secondary">
            {t.hero.cta2}
          </a>
        </motion.div>

        {/* Expertise Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="flex flex-wrap justify-center gap-3"
        >
          {t.hero.badges.map((badge, i) => (
            <span 
              key={badge}
              className="mono text-xs px-4 py-2 rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary-light)] transition-all duration-300"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {badge}
            </span>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-[var(--border)] flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Approche Section - Positionnement & Approche
const Approche = () => {
  const { t } = useLanguage();
  
  // Render subtitle with line breaks
  const renderTextWithBreaks = (text) => {
    return text.split('\n\n').map((paragraph, i) => (
      <p key={i} className={i > 0 ? "mt-4" : ""}>{paragraph}</p>
    ));
  };
  
  return (
    <AnimatedSection id="approche" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <span className="mono text-xs text-[var(--primary)] uppercase tracking-widest mb-4 block">
            {t.positioning.label}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.positioning.title} <span className="gradient-text">{t.positioning.titleHighlight}</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl mx-auto">
            {t.positioning.subtitle}
          </p>
        </div>

        {/* Two columns */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Positionnement */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="glass rounded-none p-10 card-hover relative overflow-hidden group"
            data-testid="approche-positionnement"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[var(--primary)] to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
            <h3 className="text-2xl font-bold mb-6">{t.positioning.positionTitle}</h3>
            <div className="space-y-4 text-[var(--text-secondary)] leading-relaxed">
              {renderTextWithBreaks(t.positioning.positionText)}
            </div>
          </motion.div>

          {/* Approche */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="glass rounded-none p-10 card-hover relative overflow-hidden group"
            data-testid="approche-methode"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[var(--secondary)] to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
            <h3 className="text-2xl font-bold mb-4">{t.approach.title}</h3>
            <p className="text-[var(--text-muted)] mb-6 mono text-sm">{t.approach.subtitle}</p>
            <ul className="space-y-3">
              {t.approach.steps.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)]">
                  <ChevronRight className="text-[var(--secondary)] mt-1 flex-shrink-0" size={16} />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Key figures - with animated counters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto"
        >
          {t.stats.map((stat, i) => {
            const icons = [Clock, Rocket];
            const Icon = icons[i];
            // Check if value is animatable (pure number format like "10+", "+15", "100%")
            const isAnimatable = /^(\+?)(\d+)(\+?%?)$/.test(stat.value);
            
            return (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4, borderColor: 'var(--primary)' }}
                className="text-center p-6 border border-[var(--border)] rounded-none transition-all duration-300 group"
              >
                <Icon size={24} className="mx-auto mb-3 text-[var(--secondary)] group-hover:scale-110 transition-transform" />
                <span className="text-3xl font-bold gradient-text block">
                  {isAnimatable ? (
                    <AnimatedCounter value={stat.value} />
                  ) : (
                    stat.value
                  )}
                </span>
                <span className="text-sm text-[var(--text-muted)]">{stat.label}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Expertises Section
const Expertises = () => {
  const { t } = useLanguage();
  const icons = [Settings, Search, Cpu, Database, Target];
  const colors = ["var(--primary)", "var(--secondary)", "var(--primary-light)", "var(--secondary-light)", "var(--primary)"];

  return (
    <AnimatedSection id="expertises" className="py-32 bg-[var(--bg-paper)] relative">
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div className="mb-16">
          <span className="mono text-xs text-[var(--secondary)] uppercase tracking-widest mb-4 block">
            {t.expertises.label}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.expertises.title} <span className="gradient-text">{t.expertises.titleHighlight}</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl">
            {t.expertises.subtitle}
          </p>
        </div>

        {/* Expertise Grid with stagger animation - 5 cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {t.expertises.cards.map((exp, i) => {
            const Icon = icons[i];
            const color = colors[i];
            return (
              <motion.div
                key={exp.title}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6, ease: "easeOut" }}
                whileHover={{ 
                  y: -8, 
                  boxShadow: `0 20px 40px -15px ${color}40`
                }}
                className="glass rounded-none p-8 relative group overflow-hidden cursor-pointer"
                data-testid={`expertise-${i}`}
              >
                {/* Animated gradient background on hover */}
                <motion.div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-25 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle at 50% 0%, ${color}, transparent 70%)` }}
                />
                
                {/* Animated border on hover */}
                <div className="absolute inset-0 border border-transparent group-hover:border-[var(--primary)] transition-colors duration-300 pointer-events-none" />
                
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Icon size={36} className="mb-5 transition-transform" style={{ color }} />
                </motion.div>
                <h3 className="text-lg font-bold mb-2">{exp.title}</h3>
                <p className="text-sm text-[var(--text-muted)] mb-4">{exp.subtitle}</p>
                <ul className="space-y-2">
                  {exp.items.map((item, j) => (
                    <motion.li 
                      key={j} 
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.15 + j * 0.05 }}
                      className="text-sm text-[var(--text-secondary)] flex items-start gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 transition-transform group-hover:scale-125" style={{ background: color }} />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Secteurs Section
const Secteurs = () => {
  const secteurs = [
    { icon: Atom, name: "Nucléaire" },
    { icon: Factory, name: "Pétrochimie" },
    { icon: Pickaxe, name: "Métallurgie" },
    { icon: Plane, name: "Aéronautique" },
    { icon: Factory, name: "Ferroviaire" },
    { icon: Building2, name: "BTP" },
    { icon: Gem, name: "Luxe" },
    { icon: Factory, name: "Télécoms" },
  ];

  return (
    <AnimatedSection id="secteurs" className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Marquee only */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[var(--bg-paper)] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[var(--bg-paper)] to-transparent z-10" />
          
          <div className="flex overflow-hidden">
            <div className="flex animate-marquee">
              {[...secteurs, ...secteurs].map((secteur, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-3 mx-6 text-[var(--text-secondary)] hover:text-white transition-colors whitespace-nowrap"
                >
                  <secteur.icon size={20} className="text-[var(--primary)]" />
                  <span>{secteur.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CAS CLIENT - Étude de cas détaillée (Business Transformation focus)
const CasClient = () => {
  const { t } = useLanguage();
  
  return (
    <AnimatedSection id="cas-client" className="py-32 relative">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-12">
          <span className="mono text-xs text-[var(--secondary)] uppercase tracking-widest mb-4 block">
            {t.caseStudy.label}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t.caseStudy.title} <span className="gradient-text">{t.caseStudy.titleHighlight}</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg">
            {t.caseStudy.subtitle}
          </p>
        </div>

        {/* Main case study card */}
        <div className="glass rounded-none p-8 md:p-12 relative overflow-hidden">
          {/* Decorative */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--primary)] opacity-5 blur-[100px]" />
          
          {/* Tags - high contrast colors */}
          <div className="flex flex-wrap gap-2 mb-8">
            {t.caseStudy.tags.map((tag, i) => (
              <span 
                key={tag}
                className={`mono text-xs px-3 py-1.5 rounded-full font-medium ${
                  i === 0 ? 'bg-[var(--primary)] text-white' :
                  i === 1 ? 'bg-[var(--secondary)] text-white' :
                  i === 2 ? 'bg-white/10 text-white border border-white/20' :
                  'bg-white/10 text-white border border-white/20'
                }`}
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Context */}
          <div className="mb-10">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Factory size={20} className="text-[var(--primary)]" />
              {t.caseStudy.contexte.title}
            </h3>
            <p className="text-[var(--text-secondary)] leading-relaxed">
              {t.caseStudy.contexte.text}
            </p>
          </div>

          {/* Our Role */}
          <div className="mb-10 p-6 bg-[var(--bg-subtle)] border-l-4 border-[var(--primary)]">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <UserCircle size={20} className="text-[var(--secondary)]" />
              {t.caseStudy.role.title}
            </h3>
            <div className="space-y-4">
              {t.caseStudy.role.items.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--primary)] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    {i === 0 && <Target size={16} className="text-[var(--primary)]" />}
                    {i === 1 && <Settings size={16} className="text-[var(--secondary)]" />}
                  </div>
                  <div>
                    <span className="font-semibold text-white block">{item.title}</span>
                    <span className="text-sm text-[var(--text-muted)]">{item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Objectives */}
          <div className="mb-10">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Target size={20} className="text-[var(--secondary)]" />
              {t.caseStudy.objectives.title}
            </h3>
            <ul className="grid md:grid-cols-2 gap-4">
              {t.caseStudy.objectives.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)]">
                  <CheckCircle2 size={16} className="text-[var(--secondary)] mt-1 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Methodology Steps */}
          <div className="mb-10">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Rocket size={20} className="text-[var(--primary)]" />
              {t.caseStudy.methodology.title}
            </h3>
            <div className="flex flex-wrap gap-3">
              {t.caseStudy.methodology.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] flex items-center justify-center text-white font-bold text-sm">
                    {i + 1}
                  </span>
                  <span className="text-[var(--text-secondary)] font-medium">{step}</span>
                  {i < t.caseStudy.methodology.steps.length - 1 && (
                    <ChevronRight size={16} className="text-[var(--text-muted)] mx-2" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Results & Impacts - Merged */}
          <div className="mb-10">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <BarChart3 size={20} className="text-[var(--secondary)]" />
              {t.caseStudy.results.title}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {t.caseStudy.results.items.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center p-5 bg-[var(--bg-subtle)] rounded-none"
                >
                  <span className="text-3xl md:text-4xl font-bold gradient-text block">{stat.value}</span>
                  <span className="text-sm text-white block mt-1">{stat.label}</span>
                </motion.div>
              ))}
            </div>
            {/* Impacts list */}
            {t.caseStudy.results.impacts && (
              <ul className="space-y-3 mt-6 pt-6 border-t border-[var(--border)]">
                {t.caseStudy.results.impacts.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)]">
                    <CheckCircle2 size={16} className="text-[var(--primary)] mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      </div>
    </AnimatedSection>
  );
};

// Réalisations Section
const Realisations = () => {
  const { t } = useLanguage();

  return (
    <AnimatedSection id="realisations" className="py-32 bg-[var(--bg-paper)]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16">
          <span className="mono text-xs text-[var(--secondary)] uppercase tracking-widest mb-4 block">
            {t.achievements.label}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.achievements.title} <span className="gradient-text">{t.achievements.titleHighlight}</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl">
            {t.achievements.subtitle}
          </p>
        </div>

        {/* Projects Grid - New format with challenge/intervention/impact */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.achievements.items.map((project, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="glass rounded-none p-6 card-hover group"
              data-testid={`realisation-${i}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <span className="mono text-xs text-[var(--primary)] uppercase">{project.sector}</span>
                <span className="text-xs text-[var(--text-muted)]">{project.country}</span>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-bold mb-4 group-hover:text-[var(--primary-light)] transition-colors">
                {project.title}
              </h3>
              
              {/* Details */}
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-[var(--text-muted)] block mb-1">{t.achievements.challengeLabel}</span>
                  <p className="text-[var(--text-secondary)]">{project.challenge}</p>
                </div>
                <div>
                  <span className="text-[var(--text-muted)] block mb-1">{t.achievements.interventionLabel}</span>
                  <p className="text-[var(--text-secondary)]">{project.intervention}</p>
                </div>
                <div>
                  <span className="text-[var(--secondary)] block mb-1">{t.achievements.impactLabel}</span>
                  <p className="text-[var(--text-secondary)]">{project.impact}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Cabinet Section
const Cabinet = () => {
  const { t } = useLanguage();
  
  return (
    <AnimatedSection id="cabinet" className="py-24 relative">
      <div className="max-w-6xl mx-auto px-6">
        {/* Compact Founder Section */}
        <div className="glass rounded-none p-8 md:p-12">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Small Photo with parallax effect */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex-shrink-0"
            >
              <div className="relative w-32 h-32 md:w-40 md:h-40 group">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] opacity-20 group-hover:opacity-40 transition-opacity blur-md" />
                <img
                  src="https://customer-assets.emergentagent.com/job_site-hostinger/artifacts/t3n86wg9_hicham%20-%20photo.PNG"
                  alt="Hicham El Hady - Fondateur ADAPEL"
                  className="w-full h-full object-cover rounded-full border-2 border-[var(--primary)] relative z-10"
                  data-testid="founder-image"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1 text-center md:text-left"
            >
              <div className="flex items-center gap-3 justify-center md:justify-start mb-3">
                <Logo className="h-8" />
                {t.cabinet.label && (
                  <span className="mono text-xs text-[var(--text-muted)] uppercase tracking-widest">
                    {t.cabinet.label}
                  </span>
                )}
              </div>
              
              <h3 className="text-2xl font-bold mb-3">{t.cabinet.name}</h3>
              
              <p className="text-[var(--text-secondary)] mb-6 max-w-xl">{t.cabinet.bio}</p>

              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a 
                  href="https://www.linkedin.com/in/hichamel-hady/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 btn-ghost text-sm py-2 px-4"
                  data-testid="linkedin-link"
                >
                  <Linkedin size={16} className="text-[var(--secondary)]" />
                  {t.cabinet.linkedin}
                </a>
                <a 
                  href="mailto:contact@adapel.fr"
                  className="inline-flex items-center gap-2 btn-primary text-sm py-2 px-4"
                >
                  <Mail size={16} />
                  {t.cabinet.contact}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Contact Section
const Contact = () => {
  const { t, language } = useLanguage();

  return (
    <AnimatedSection id="contact" className="py-24 bg-[var(--bg-paper)] relative">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[var(--primary)] opacity-5 blur-[120px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Main CTA */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.contact.title} <span className="gradient-text">{t.contact.titleHighlight}</span> ?
          </h2>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto mb-8">
            {t.contact.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* LinkedIn - Primary CTA */}
            <a 
              href="https://www.linkedin.com/in/hichamel-hady/" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-8 py-4 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              data-testid="linkedin-cta"
            >
              <Linkedin size={22} />
              {t.contact.cta2}
            </a>
            
            {/* Email - Secondary with copy */}
            <button 
              onClick={() => {
                navigator.clipboard.writeText('contact@adapel.fr');
                alert(language === 'fr' ? 'Email copié !' : 'Email copied!');
              }}
              className="btn-ghost flex items-center gap-2 group"
              data-testid="email-copy"
            >
              <Mail size={18} />
              <span>{t.contact.cta1}</span>
              <Copy size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>

        {/* Testimonials - 2x2 grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {t.contact.testimonials.map((testimonial, i) => (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass rounded-none p-5 relative group hover:border-[var(--primary)] transition-colors"
            >
              <div className="absolute top-2 left-3 text-3xl text-[var(--primary)] opacity-30 font-serif">
                &ldquo;
              </div>
              <p className="text-[var(--text-secondary)] text-sm italic mb-3 pl-5 line-clamp-3">
                {testimonial.quote}
              </p>
              <div className="flex items-center gap-2 pl-5">
                <span className="font-medium text-white text-sm">{testimonial.author}</span>
                <span className="text-[var(--text-muted)] text-xs">— {testimonial.role}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

// Footer
const Footer = ({ onShowLegal }) => {
  const { t } = useLanguage();
  
  return (
    <footer className="py-8 border-t border-[var(--border)]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo className="h-8" />
            <span className="text-[var(--text-muted)] text-sm">
              © {new Date().getFullYear()} ADAPEL — {t.footer.rights}
            </span>
          </div>

          <div className="flex items-center gap-6">
            <button 
              onClick={onShowLegal}
              className="text-sm text-[var(--text-muted)] hover:text-white transition-colors"
              data-testid="legal-link"
            >
              {t.footer.legal}
            </button>
            <a 
              href="https://www.linkedin.com/in/hichamel-hady/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--secondary)] transition-colors"
            >
              <Linkedin size={18} />
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [showLegal, setShowLegal] = useState(false);

  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Show Legal Page
  if (showLegal) {
    return (
      <LanguageProvider>
        <LegalPage onBack={() => setShowLegal(false)} />
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <AppContent onShowLegal={() => setShowLegal(true)} />
    </LanguageProvider>
  );
}

// App Content Component
const AppContent = ({ onShowLegal }) => {
  return (
    <div className="App">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main>
        <Hero />
        <Approche />
        <Expertises />
        <Secteurs />
        <Realisations />
        <CasClient />
        <Cabinet />
        <Contact />
      </main>

      {/* Footer */}
      <Footer onShowLegal={onShowLegal} />
    </div>
  );
}

export default App;
